// Declared in SharedModule next to DesignContextComponent, like its hosts.
/* eslint-disable @angular-eslint/prefer-standalone */
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MapFeatureLink } from '../../models/map-feature-link';
import { captureNeedsRetake, captureViewerAttachment, linkCaptureImage } from '../../helpers/design-context';
import { MarkupService } from '../../services/markup.service';
import { AppService } from '../../services/app.service';

/**
 * The crew's marked-up map capture in the LOCATION / STATION minimap slot (spec addendum §11). Hosts swap it in for
 * the minimap exactly when `linkCaptureImage(link)` is non-null, at the minimap's size; it renders nothing otherwise.
 * By default it is a plain image (the host's own click area opens the picker, as the minimap does); with an
 * `actionLabel` it is a button that emits `activate` (the picker strip's Edit markup, a station slot's picker).
 * Badges sit bottom-left so a station range's FROM / TO labels (top corners) stay visible.
 */
@Component({
  selector: 'app-design-capture-preview',
  templateUrl: './design-capture-preview.component.html',
  styleUrls: ['./design-capture-preview.component.scss'],
  standalone: false,
})
export class DesignCapturePreviewComponent {
  private cdr = inject(ChangeDetectorRef);
  private markupService = inject(MarkupService);
  private appService = inject(AppService);

  @Input() link: MapFeatureLink | null = null;
  /** The minimap's height in the host. */
  @Input() height = '80px';
  /** Makes the image a button with this accessible name; null (the default) keeps it a plain image. */
  @Input() actionLabel: string | null = null;
  @Input() disabled = false;
  /** false when the host lists the badges itself (the picker strip, whose Map moved follows the live pin). */
  @Input() showBadges = true;
  /**
   * The location slot (Sri 2026-09-28): a "View map markup" button that opens the full marked-up picture in the app's
   * viewer, read-only. It stops the click so the host row's own click (the picker) does not also fire; hosts reach
   * the picker from the rest of the row or the design-context card's "Edit location".
   */
  @Input() viewOnClick = false;
  @Output() activate = new EventEmitter<void>();

  /**
   * Previews of capture markups saved elsewhere on the page, by attachment uuid: a host holding the fetched link
   * never hears about an edit made in another panel (same as `pictures-section`).
   */
  private previews: Record<string, string> = {};
  private readonly markupSavedSub = this.markupService.markupSaved$.pipe(takeUntilDestroyed()).subscribe(({ attachmentId, previewDataUrl }) => {
    if (!attachmentId || !previewDataUrl) return;
    this.previews[attachmentId] = previewDataUrl;
    if (attachmentId === this.link?.capture?.attachment_uuid) this.cdr.markForCheck();
  });

  /** The full-size preview before the small thumb (sharper in the slot), a markup saved on this page first. */
  get src(): string | null {
    const image = linkCaptureImage(this.link);
    if (!image) return null;
    return this.previews[this.link.capture.attachment_uuid] || this.link.capture.preview_url || image;
  }
  get mapMoved(): boolean { return captureNeedsRetake(this.link); }

  view(event: Event): void {
    event.stopPropagation();
    const capture = this.link?.capture;
    const attachment = captureViewerAttachment(capture, capture ? this.previews[capture.attachment_uuid] : null);
    if (attachment) this.appService.openCarousel(event, [attachment] as never[], attachment as never, false);
  }
}
